<script setup lang="ts">
    import type { AgentReasoning } from 'models'
    import { useI18n } from 'vue-i18n'

    const { t: $t } = useI18n({ useScope: 'global' })

    defineProps<{
        activeProvider: string
    }>()

    const reasoning = defineModel<AgentReasoning>('reasoning', {
        default: undefined,
    })
</script>

<template>
    <!-- OpenAI -->
    <template v-if="activeProvider === 'openai'">
        <VvCombobox
            :model-value="reasoning?.openai?.reasoningEffort"
            name="reasoning.openai.reasoningEffort"
            icon="ri:brain-line"
            strategy="fixed"
            :label="$t('label.reasoningEffort')"
            :placeholder="$t('placeholder.select')"
            :options="[
                { label: 'None', value: 'none' },
                { label: 'Low', value: 'low' },
                { label: 'Medium', value: 'medium' },
                { label: 'High', value: 'high' },
                { label: 'XHigh', value: 'xhigh' },
            ]"
            @update:model-value="
                reasoning = {
                    ...reasoning,
                    openai: {
                        ...reasoning?.openai,
                        reasoningEffort: $event,
                    },
                }
            " />
        <VvCombobox
            v-if="
                reasoning?.openai?.reasoningEffort &&
                reasoning.openai.reasoningEffort !== 'none'
            "
            :model-value="reasoning?.openai?.reasoningSummary"
            name="reasoning.openai.reasoningSummary"
            strategy="fixed"
            icon="ri:file-text-line"
            :label="$t('label.reasoningSummary')"
            :placeholder="$t('placeholder.select')"
            class="mb-0"
            :options="[
                { label: 'Auto', value: 'auto' },
                { label: 'Detailed', value: 'detailed' },
                { label: 'Concise', value: 'concise' },
            ]"
            @update:model-value="
                reasoning = {
                    ...reasoning,
                    openai: {
                        ...reasoning?.openai,
                        reasoningSummary: $event,
                    },
                }
            " />
    </template>
    <!-- Anthropic -->
    <template v-else-if="activeProvider === 'anthropic'">
        <VvCombobox
            :model-value="reasoning?.anthropic?.type"
            name="reasoning.anthropic.type"
            icon="ri:brain-line"
            strategy="fixed"
            :label="$t('label.thinkingType')"
            :placeholder="$t('placeholder.select')"
            :options="[
                { label: 'Adaptive', value: 'adaptive' },
                { label: 'Enabled (legacy)', value: 'enabled' },
                { label: 'Disabled', value: 'disabled' },
            ]"
            @update:model-value="
                reasoning = {
                    ...reasoning,
                    anthropic: {
                        ...reasoning?.anthropic,
                        type: $event,
                    },
                }
            " />
        <VvCombobox
            v-if="reasoning?.anthropic?.type === 'adaptive'"
            :model-value="reasoning?.anthropic?.effort"
            name="reasoning.anthropic.effort"
            strategy="fixed"
            icon="ri:speed-line"
            :label="$t('label.thinkingEffort')"
            :placeholder="$t('placeholder.select')"
            :options="[
                { label: 'Low', value: 'low' },
                { label: 'Medium', value: 'medium' },
                { label: 'High', value: 'high' },
                { label: 'XHigh', value: 'xhigh' },
                { label: 'Max', value: 'max' },
            ]"
            @update:model-value="
                reasoning = {
                    ...reasoning,
                    anthropic: {
                        ...reasoning?.anthropic,
                        effort: $event,
                    },
                }
            " />
        <VvCombobox
            v-if="reasoning?.anthropic?.type === 'adaptive'"
            :model-value="reasoning?.anthropic?.display"
            name="reasoning.anthropic.display"
            strategy="fixed"
            icon="ri:eye-line"
            :label="$t('label.thinkingDisplay')"
            :placeholder="$t('placeholder.select')"
            :options="[
                { label: 'Summarized', value: 'summarized' },
                { label: 'Omitted', value: 'omitted' },
            ]"
            @update:model-value="
                reasoning = {
                    ...reasoning,
                    anthropic: {
                        ...reasoning?.anthropic,
                        display: $event,
                    },
                }
            " />
        <VvInputText
            v-if="reasoning?.anthropic?.type === 'enabled'"
            :model-value="reasoning?.anthropic?.budgetTokens"
            name="reasoning.anthropic.budgetTokens"
            type="number"
            min="1"
            step="1000"
            :label="$t('label.budgetTokens')"
            :placeholder="$t('placeholder.insert')"
            @update:model-value="
                reasoning = {
                    ...reasoning,
                    anthropic: {
                        ...reasoning?.anthropic,
                        budgetTokens: $event ? Number($event) : undefined,
                    },
                }
            " />
    </template>
    <!-- Google -->
    <template v-else-if="activeProvider === 'google'">
        <VvCombobox
            :model-value="reasoning?.google?.thinkingLevel"
            name="reasoning.google.thinkingLevel"
            icon="ri:brain-line"
            strategy="fixed"
            :label="$t('label.thinkingLevel')"
            :placeholder="$t('placeholder.select')"
            :options="[
                { label: 'Minimal', value: 'minimal' },
                { label: 'Low', value: 'low' },
                { label: 'Medium', value: 'medium' },
                { label: 'High', value: 'high' },
            ]"
            @update:model-value="
                reasoning = {
                    ...reasoning,
                    google: {
                        ...reasoning?.google,
                        thinkingLevel: $event,
                    },
                }
            " />
        <VvInputText
            :model-value="reasoning?.google?.thinkingBudget"
            name="reasoning.google.thinkingBudget"
            type="number"
            min="0"
            step="1024"
            :label="$t('label.thinkingBudget')"
            :placeholder="$t('placeholder.insert')"
            @update:model-value="
                reasoning = {
                    ...reasoning,
                    google: {
                        ...reasoning?.google,
                        thinkingBudget: $event ? Number($event) : undefined,
                    },
                }
            " />
        <VvCheckbox
            :model-value="reasoning?.google?.includeThoughts ?? false"
            name="reasoning.google.includeThoughts"
            class="flex-nowrap mb-0"
            :value="true"
            :unchecked-value="false"
            switch
            @update:model-value="
                reasoning = {
                    ...reasoning,
                    google: {
                        ...reasoning?.google,
                        includeThoughts: $event,
                    },
                }
            ">
            <div class="flex flex-col ml-12 gap-2">
                <strong class="font-semibold">
                    {{ $t('label.includeThoughts') }}
                </strong>
                <small class="text-12 text-word-3">
                    {{ $t('hint.includeThoughts') }}
                </small>
            </div>
        </VvCheckbox>
    </template>
    <!-- DeepSeek -->
    <template v-else-if="activeProvider === 'deepseek'">
        <VvCombobox
            :model-value="reasoning?.deepseek?.type"
            name="reasoning.deepseek.type"
            icon="ri:brain-line"
            strategy="fixed"
            :label="$t('label.thinking')"
            :placeholder="$t('placeholder.select')"
            :options="[
                { label: $t('label.enabled'), value: 'enabled' },
                { label: $t('label.disabled'), value: 'disabled' },
            ]"
            @update:model-value="
                reasoning = {
                    ...reasoning,
                    deepseek: { type: $event },
                }
            " />
    </template>
    <!-- Other providers -->
    <template v-else>
        <small class="text-12 text-word-3">
            {{ $t('hint.reasoningNotSupported') }}
        </small>
    </template>
</template>
